import { AgeVerificationMethod, AgeVerificationMethodPriority, AgeVerificationStatus } from "@next-platform/packages-ts-types";
import { Badge, BadgeVariant, Button, ButtonSize, ButtonVariant, Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { NotificationType, useAgeVerification, useTranslation, useUserInfo } from "@next-platform/client-common-core";
import { useEffect, useState } from "react";

import useToast from "@/hooks/useToast";

import Trans from "../Trans/Trans";
import getAgeVerificationMethodOptions from "./utils/getAgeVerificationMethodOptions";

interface IAgeVerificationMethodsProps {
  priority?: AgeVerificationMethodPriority;
  skipStatusCheck?: boolean;
}

// Extend the base interface to include methodRef until it's available in the package
interface IExtendedAgeVerificationMethod extends AgeVerificationMethod {
  methodRef: string;
}

// The updated age verification page logic:
// - On load, if age verification is needed, call onUserGetAgeVerificationMethods to retrieve available verification methods (with methodRefs).
// - List the available methods for the user.
// - When a user clicks "Choose", call onUserPostAgeVerificationInitialize with the selected method's methodRef.
// - When the initialize API responds, redirect user to the providerUrl returned in the response.
const AgeVerificationMethods = ({ priority, skipStatusCheck = false }: IAgeVerificationMethodsProps) => {
  const [isLoadingMethod, setIsLoadingMethod] = useState(false);
  // New: Instead of a boolean, store methodRef of loading method (or null/undefined)
  const [isLoadingInitialize, setIsLoadingInitialize] = useState<string | null>(null);
  const [availableMethods, setAvailableMethods] = useState<IExtendedAgeVerificationMethod[] | undefined | null>(undefined);
  const { displayToast } = useToast();
  const { onUserGetAgeVerificationMethods, onUserPostAgeVerificationInitialize } = useAgeVerification();
  const { userProfile } = useUserInfo();
  const { t } = useTranslation();
  const methodOptions = getAgeVerificationMethodOptions(t);
  const verificationStatus = userProfile?.age_verification?.status;

  // Fetch the available verification methods (with methodRef) only if required
  useEffect(() => {
    const shouldSkipFetch = () => {
      if (availableMethods !== undefined) return true;
      if (skipStatusCheck) return false;
      if (!verificationStatus) return true;
      return verificationStatus === AgeVerificationStatus.DISABLED || verificationStatus === AgeVerificationStatus.VERIFIED;
    };

    const fetchMethods = async () => {
      if (shouldSkipFetch()) return;

      setIsLoadingMethod(true);
      const requestParams = priority ? { priority: priority } : {};
      const result = await onUserGetAgeVerificationMethods(requestParams);

      setIsLoadingMethod(false);
      if (result?.error) {
        displayToast({ message: result.error, type: NotificationType.default });
        setAvailableMethods(null);
      } else if (result?.data?.methods) {
        setAvailableMethods(result.data.methods as unknown as IExtendedAgeVerificationMethod[]);
      }
    };

    fetchMethods();
  }, [availableMethods, verificationStatus]);

  // Handler for selecting a method: initialize verification and redirect to providerUrl
  const onClickMethod = async (method: IExtendedAgeVerificationMethod) => {
    if (!method?.methodRef) {
      displayToast({ message: t("ageVerification:error.missingMethodRef"), type: NotificationType.error });
      return;
    }

    setIsLoadingInitialize(method.methodRef);
    const result = await onUserPostAgeVerificationInitialize({ methodRef: method.methodRef });
    // We intentionally do not unset isLoadingMethod until the redirection occurs or error happens.

    if (result?.error) {
      displayToast({ message: result.error, type: NotificationType.default });
      setIsLoadingInitialize(null);
      return;
    }

    const providerUrl = result.data?.providerUrl;
    if (providerUrl) {
      // Safely launch the age verification process in a new page
      window.open(providerUrl, "_self");
    } else {
      displayToast({ message: t("ageVerification:error.noProviderUrl"), type: NotificationType.error });
      setIsLoadingInitialize(null);
    }
  };

  if (isLoadingMethod) {
    return <Spinner />;
  }

  if (Array.isArray(availableMethods) && availableMethods.length > 0) {
    return (
      <div className="flex flex-col gap-sm">
        {Array.isArray(availableMethods) &&
          availableMethods.length > 0 &&
          availableMethods.map((method: IExtendedAgeVerificationMethod) => {
            if (!methodOptions?.[method.type]) {
              return null;
            }
            const provider = method?.methodRef?.split("-")?.[0];
            return (
              <div
                key={method.methodRef || methodOptions[method.type]?.label}
                className="flex flex-row gap-4 justify-between border-gray-8 border-1 border-solid rounded-md p-md bg-gray-3 items-center"
              >
                <div className="flex flex-col gap-sm">
                  <div className="flex flex-row gap-sm">
                    <Text variant={TextVariants.LABEL_LARGE_BOLD}>{methodOptions[method.type]?.label}</Text>
                    {provider ? (
                      <Badge showDot={false} variant={BadgeVariant.DEFAULT}>
                        {provider}
                      </Badge>
                    ) : null}
                  </div>
                  <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-11">
                    {methodOptions[method.type]?.description}
                  </Text>
                </div>
                <Button
                  variant={priority === AgeVerificationMethodPriority.FALLBACK ? ButtonVariant.SECONDARY : ButtonVariant.CONTAINED}
                  size={ButtonSize.MEDIUM}
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- unavoidable inside .map()
                  onClick={() => onClickMethod(method)}
                  loading={isLoadingInitialize === method.methodRef}
                  disabled={!!isLoadingInitialize && isLoadingInitialize !== method.methodRef}
                >
                  {priority === AgeVerificationMethodPriority.FALLBACK ? (
                    <Trans i18nKey="ageVerification:section.failure.cta.tryAgain" />
                  ) : (
                    <Trans i18nKey="ageVerification:method.cta.getStarted" />
                  )}
                </Button>
              </div>
            );
          })}
      </div>
    );
  }

  return (
    <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>
      <Trans i18nKey="ageVerification:method.noOptionsAvailable" />
    </Text>
  );
};

export default AgeVerificationMethods;
